import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, RefreshCw, Copy, FileText, ChevronDown, ChevronRight, Download, Share2, Search } from "lucide-react";
import { avatarForId } from "@/lib/avatars";
import { timeAgo } from "@/lib/lifeos";

export default function DeliverablesPanel({ onClose }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");
  const [sourceF, setSourceF] = useState("all");

  const load = () => { setLoading(true); api.getDeliverables().then(setItems).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); };

  const download = (t) => {
    const safe = (t.instruction || "deliverable").replace(/[^a-z0-9]+/gi, "-").slice(0, 40);
    const blob = new Blob([`# ${t.instruction}\n\nBy ${t.agent_name} · ${t.department}\n\n${t.deliverable}`], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${safe}.md`; a.click();
    URL.revokeObjectURL(url);
  };

  const share = async (t) => {
    try {
      const { share_id } = await api.shareDeliverable(t.id);
      const link = `${window.location.origin}/s/${share_id}`;
      await navigator.clipboard.writeText(link);
      toast.success("Share link copied", { description: link });
    } catch { toast.error("Could not create share link"); }
  };

  const departments = useMemo(() => Array.from(new Set((items || []).map((t) => t.department).filter(Boolean))), [items]);
  const filtered = useMemo(() => {
    return (items || []).filter((t) => {
      if (dept !== "all" && t.department !== dept) return false;
      if (sourceF !== "all" && (t.source || "manual") !== sourceF) return false;
      if (q) {
        const hay = `${t.instruction} ${t.deliverable} ${t.agent_name} ${t.department}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [items, q, dept, sourceF]);

  const selectCls = "bg-[#0b0e14] border border-white/10 rounded-lg px-2 h-9 text-xs text-zinc-200 font-tactical focus:outline-none focus:ring-2 focus:ring-cyan-400/40";

  return (
    <div className="max-w-4xl mx-auto pt-8 px-4 pb-24">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display uppercase tracking-widest text-cyan-300 text-lg flex items-center gap-2"><FileText size={18} /> Deliverables</h2>
        <div className="flex items-center gap-2">
          <button data-testid="deliverables-refresh" onClick={load} className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /></button>
          <button data-testid="deliverables-close-button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400"><X size={16} /></button>
        </div>
      </div>

      {/* search + filters */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="deliverables-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search deliverables..." className="w-full bg-[#0b0e14] border border-white/10 rounded-lg pl-8 pr-3 h-9 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40" />
        </div>
        <select data-testid="deliverables-dept-filter" className={selectCls} value={dept} onChange={(e) => setDept(e.target.value)}>
          <option value="all" className="bg-[#0b0e14]">All departments</option>
          {departments.map((d) => <option key={d} value={d} className="bg-[#0b0e14]">{d}</option>)}
        </select>
        <select data-testid="deliverables-source-filter" className={selectCls} value={sourceF} onChange={(e) => setSourceF(e.target.value)}>
          <option value="all" className="bg-[#0b0e14]">All sources</option>
          <option value="manual" className="bg-[#0b0e14]">Manual</option>
          <option value="autopilot" className="bg-[#0b0e14]">Autopilot</option>
        </select>
      </div>

      {items && filtered.length === 0 && (
        <div className="text-center text-zinc-600 font-mono text-sm py-16">{items.length === 0 ? "No deliverables yet. Assign a task to an agent." : "No matches for your filters."}</div>
      )}

      <div className="space-y-3">
        {filtered.map((t) => {
          const isOpen = open[t.id];
          return (
            <div key={t.id} data-testid="deliverable-item" className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <div className="flex items-center gap-3 p-3">
                <span className="rounded-full overflow-hidden shrink-0" style={{ width: 36, height: 36, border: `2px solid ${t.accent}`, background: "#0b0e14" }}><img src={avatarForId(t.agent_id)} alt="" className="w-full h-full object-cover object-top" /></span>
                <button onClick={() => setOpen((o) => ({ ...o, [t.id]: !o[t.id] }))} className="min-w-0 flex-1 text-left">
                  <div className="text-sm text-zinc-100 truncate font-medium">{t.instruction}</div>
                  <div className="text-[11px] font-mono text-zinc-500 truncate">{t.agent_name} · {t.department} · {timeAgo(t.created_at)} ago{t.source === "autopilot" ? " · autopilot" : ""}</div>
                </button>
                <button data-testid="deliverable-download" onClick={() => download(t)} title="Download .md" className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-cyan-300 shrink-0"><Download size={15} /></button>
                <button data-testid="deliverable-share" onClick={() => share(t)} title="Copy share link" className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-cyan-300 shrink-0"><Share2 size={15} /></button>
                <button onClick={() => setOpen((o) => ({ ...o, [t.id]: !o[t.id] }))} className="w-8 h-8 grid place-items-center rounded-lg text-zinc-500 shrink-0">{isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button>
              </div>
              {isOpen && (
                <div className="px-3 pb-3 anim-slidein">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-mono" style={{ color: t.accent }}>Deliverable</span>
                    <button onClick={() => copy(t.deliverable)} className="text-[10px] font-mono text-zinc-400 hover:text-cyan-300 flex items-center gap-1"><Copy size={11} /> Copy</button>
                  </div>
                  <pre className="text-[12px] leading-relaxed text-zinc-200 whitespace-pre-wrap font-sans bg-[#0b0e14] border border-white/10 rounded-lg p-3 max-h-96 overflow-y-auto thin-scroll">{t.deliverable}</pre>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
